import {useEffect, useEffectEvent} from 'react';

type UseSourcingIntervalPollerProps = {
  enabled: boolean;
  pollingInterval: number;
  doPoll: () => void | Promise<void>;
  /**
   * When this changes while enabled, restart the interval (immediate poll + onRestart).
   * Use for user-driven resets (filter/load key, adId).
   */
  resetKey?: string | number;
  /**
   * When this changes while enabled, fire one immediate poll without restarting the interval.
   * Use for membership growth (e.g. real-time prepend) that must not reset a give-up window.
   */
  tickKey?: string | number;
  /** Runs at the start of each interval restart (enabled / resetKey). */
  onRestart?: () => void;
  /** Runs before the immediate poll when tickKey changes (not on interval restart). */
  onTick?: () => void;
};

/**
 * Shared interval + visibility poller for sourcing list/detail.
 * Owns the only approved-exception useEffects for this pattern — call sites should not add more.
 */
export function useSourcingIntervalPoller(props: UseSourcingIntervalPollerProps) {
  const doPoll = useEffectEvent(() => props.doPoll());
  const handleRestart = useEffectEvent(() => {
    props.onRestart?.();
  });
  const handleTick = useEffectEvent(() => {
    props.onTick?.();
  });

  // useEffect: window setInterval + visibilitychange. No approved-pattern equivalent.
  useEffect(
    function runIntervalPoll() {
      if (!props.enabled) {
        return;
      }

      handleRestart();

      let intervalId: number | undefined;

      function maybePoll() {
        if (document.hidden) {
          return;
        }
        void doPoll();
      }

      function startInterval() {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
        }
        intervalId = window.setInterval(maybePoll, props.pollingInterval);
      }

      maybePoll();
      startInterval();

      function onVisibilityChange() {
        if (!document.hidden) {
          void doPoll();
          startInterval();
        }
      }
      document.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        if (intervalId !== undefined) {
          window.clearInterval(intervalId);
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    },
    [props.enabled, props.pollingInterval, props.resetKey]
  );

  // useEffect: tick without resetting the interval window (e.g. prepend-only).
  useEffect(
    function pollOnTickKeyChange() {
      if (!props.enabled || props.tickKey === undefined) {
        return;
      }

      handleTick();
      void doPoll();
    },
    [props.enabled, props.tickKey]
  );
}
